import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { computed } from 'mobx'
import { observer } from 'mobx-react'
import moment from 'moment'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import ButtonGroup from '@mui/material/ButtonGroup'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Alert from '@mui/material/Alert'
import TextField from '@mui/material/TextField'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import makeStyles from '@mui/styles/makeStyles'
import DateRangeIcon from '@mui/icons-material/DateRange'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import Page from 'admin/components/page'
import DateRangePicker from 'admin/components/date-range-picker'
import CustomDataGrid from 'admin/components/data-grid'
import { useStores } from 'admin/hooks'
import { formatDate } from 'admin/utils/helper'

const METRIC_OPTIONS = [
  { key: 'revenue', label: 'Revenue', valueKey: 'totalOrderAmount' },
  { key: 'orders', label: 'Orders', valueKey: 'orderCount' },
  { key: 'avgOrder', label: 'Avg Order', valueKey: 'averageOrderAmount' },
  { key: 'itemCount', label: 'Item Count', valueKey: 'invoiceItemCount' },
  { key: 'margin', label: 'Margin', valueKey: 'estimatedMargin' },
]

const PRESETS = [
  {
    key: '3m',
    label: 'Last 3M',
    getRange: () => ({
      startDate: moment().subtract(2, 'months').startOf('month').toDate(),
      endDate: moment().endOf('month').toDate(),
    }),
  },
  {
    key: '6m',
    label: '6M',
    getRange: () => ({
      startDate: moment().subtract(5, 'months').startOf('month').toDate(),
      endDate: moment().endOf('month').toDate(),
    }),
  },
  {
    key: '12m',
    label: '12M',
    getRange: () => ({
      startDate: moment().subtract(11, 'months').startOf('month').toDate(),
      endDate: moment().endOf('month').toDate(),
    }),
  },
  {
    key: 'ytd',
    label: 'YTD',
    getRange: () => ({
      startDate: moment().startOf('year').toDate(),
      endDate: moment().endOf('month').toDate(),
    }),
  },
]

const useStyles = makeStyles((theme) => ({
  boxDateRange: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  dateRangePicker: {
    border: `1px solid ${theme.color.lightGray}`,
    borderRadius: '4px',
  },
  spanDate: {
    marginLeft: '0.75rem',
  },
  cardTitle: {
    marginBottom: '0.75rem',
  },
  chartCard: {
    minHeight: 420,
  },
  legendItem: {
    cursor: 'pointer',
    border: `1px solid ${theme.color.lightGray}`,
  },
  selectedLegend: {
    borderColor: theme.color.primary,
    background: theme.color.paleBlue,
  },
}))

const integerFormatter = new Intl.NumberFormat('en-US')
const currencyFormatter = new Intl.NumberFormat('en-SG', {
  style: 'currency',
  currency: 'SGD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

const numberCell = (value) => integerFormatter.format(Number(value || 0))
const currencyCell = (value) => currencyFormatter.format(Number(value || 0))

const columns = [
  { field: 'name', headerName: 'Group', minWidth: 200, flex: 1, sortable: false },
  {
    field: 'orderCount',
    headerName: 'Invoice Order Count',
    minWidth: 180,
    sortable: false,
    renderCell: (params) => numberCell(params.row.orderCount),
  },
  {
    field: 'invoiceItemCount',
    headerName: 'Invoice Item Count',
    minWidth: 180,
    sortable: false,
    renderCell: (params) => numberCell(params.row.invoiceItemCount),
  },
  {
    field: 'averageOrderAmount',
    headerName: 'Avg Invoice Order Amount',
    minWidth: 210,
    sortable: false,
    renderCell: (params) => currencyCell(params.row.averageOrderAmount),
  },
  {
    field: 'totalOrderAmount',
    headerName: 'Total Invoice Order Amount',
    minWidth: 210,
    sortable: false,
    renderCell: (params) => currencyCell(params.row.totalOrderAmount),
  },
  {
    field: 'estimatedMargin',
    headerName: 'Estimated Margin',
    minWidth: 180,
    sortable: false,
    renderCell: (params) => currencyCell(params.row.estimatedMargin),
  },
]

const rankingColumns = [
  { field: 'name', headerName: 'Group', flex: 1, minWidth: 180, sortable: false },
  {
    field: 'value',
    headerName: 'Selected Metric',
    minWidth: 160,
    sortable: false,
    renderCell: (params) => currencyCell(params.row.value),
  },
  {
    field: 'momDelta',
    headerName: 'MoM Delta %',
    minWidth: 140,
    sortable: false,
    renderCell: (params) => `${params.row.momDelta.toFixed(2)}%`,
  },
]

const Analytics = () => {
  const classes = useStyles()
  const { reportStore, notificationStore } = useStores()

  const [anchorEl, setAnchorEl] = useState(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [viewMode, setViewMode] = useState('trend')
  const [groupBy, setGroupBy] = useState('outlet')
  const [metric, setMetric] = useState('revenue')
  const [preset, setPreset] = useState('6m')
  const [legendSearch, setLegendSearch] = useState('')
  const [isolatedSeriesId, setIsolatedSeriesId] = useState(null)
  const [pinnedSeriesId, setPinnedSeriesId] = useState(null)
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [summaryData, setSummaryData] = useState({ outlet: [], driver: [] })
  const [trendData, setTrendData] = useState(null)
  const [rangeDate, setRangeDate] = useState(() => {
    const initialRange = PRESETS.find((item) => item.key === '6m').getRange()
    return {
      key: 'selection',
      ...initialRange,
    }
  })

  const handleClick = useCallback((event) => setAnchorEl(event.currentTarget), [])
  const handleClose = useCallback(() => setAnchorEl(null), [])

  const handleSelect = useCallback((values) => {
    const { startDate, endDate } = values.selection
    setPreset('custom')
    setRangeDate((prev) => ({ ...prev, startDate, endDate }))
  }, [])

  const applyPreset = useCallback((presetKey) => {
    const selectedPreset = PRESETS.find((item) => item.key === presetKey)
    if (!selectedPreset) return
    const dates = selectedPreset.getRange()
    setPreset(presetKey)
    setRangeDate((prev) => ({ ...prev, ...dates }))
  }, [])

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const [summaryResponse, trendResponse] = await Promise.all([
          reportStore.fetchInvoiceAnalytics({
            startDate: rangeDate.startDate,
            endDate: rangeDate.endDate,
            mode: 'summary',
          }),
          reportStore.fetchInvoiceAnalytics({
            startDate: rangeDate.startDate,
            endDate: rangeDate.endDate,
            mode: 'trend',
            groupBy,
            granularity: 'month',
          }),
        ])
        if (summaryResponse)
          setSummaryData({
            outlet: summaryResponse.outlet || [],
            driver: summaryResponse.driver || [],
          })
        if (trendResponse) {
          setTrendData(trendResponse)
          const latestPeriod = trendResponse.periods?.[trendResponse.periods.length - 1]
          setSelectedPeriod((current) =>
            trendResponse.periods?.includes(current) ? current : latestPeriod,
          )
        }
      } catch (error) {
        notificationStore.setError(error)
      }
    }

    fetchAnalytics()
  }, [rangeDate, groupBy, reportStore, notificationStore])

  const date = computed(
    () => `${formatDate(rangeDate.startDate)} - ${formatDate(rangeDate.endDate)}`,
  ).get()
  const selectedMetric = useMemo(
    () => METRIC_OPTIONS.find((item) => item.key === metric) || METRIC_OPTIONS[0],
    [metric],
  )
  const trendModel = useMemo(
    () => reportStore.buildTrendChartModel(trendData, metric, 8),
    [reportStore, trendData, metric],
  )

  const visibleSeries = useMemo(() => {
    const keyword = legendSearch.trim().toLowerCase()
    return trendModel.chartSeries.filter((item) => {
      const keywordMatch = !keyword || item.name.toLowerCase().includes(keyword)
      const isolateMatch = !isolatedSeriesId || item.id === isolatedSeriesId
      return keywordMatch && isolateMatch
    })
  }, [trendModel.chartSeries, legendSearch, isolatedSeriesId])

  const chartRows = useMemo(() => {
    const visibleKeys = new Set(visibleSeries.map((item) => item.dataKey))
    return trendModel.chartRows.map((row) => {
      return Object.keys(row).reduce(
        (acc, key) => {
          if (key === 'period' || visibleKeys.has(key)) {
            acc[key] = row[key]
          }
          return acc
        },
        { period: row.period },
      )
    })
  }, [trendModel.chartRows, visibleSeries])

  const rankedRows = useMemo(() => {
    if (!selectedPeriod) return []

    return trendModel.chartSeries
      .map((series) => {
        const currentIndex = series.points.findIndex((point) => point.period === selectedPeriod)
        const current = series.points[currentIndex] || {}
        const previous = series.points[currentIndex - 1] || {}
        const currentValue = Number(current[selectedMetric.valueKey] || 0)
        const previousValue = Number(previous[selectedMetric.valueKey] || 0)
        const momDelta =
          previousValue > 0 ? ((currentValue - previousValue) / previousValue) * 100 : 0

        return {
          id: series.id,
          name: series.name,
          value: currentValue,
          momDelta,
        }
      })
      .sort((a, b) => b.value - a.value)
  }, [trendModel.chartSeries, selectedPeriod, selectedMetric.valueKey])

  const detailRows = groupBy === 'outlet' ? summaryData.outlet : summaryData.driver

  const renderTooltip = useCallback(
    (tooltipProps) => {
      const { active, payload, label } = tooltipProps
      if (!active || !payload?.length) return null

      return (
        <Card>
          <CardContent>
            <Typography variant="subtitle2">{label}</Typography>
            {payload.map((entry) => {
              const series = trendModel.chartSeries.find((item) => item.dataKey === entry.dataKey)
              const point = series?.points?.find((item) => item.period === label)
              if (!series || !point) return null
              return (
                <Box
                  key={entry.dataKey}
                  sx={{ mt: 1 }}
                >
                  <Typography variant="body2">
                    <strong>{series.name}</strong>
                  </Typography>
                  <Typography variant="caption">
                    Revenue: {currencyCell(point.totalOrderAmount)}
                  </Typography>
                  <br />
                  <Typography variant="caption">Orders: {numberCell(point.orderCount)}</Typography>
                  <br />
                  <Typography variant="caption">
                    Avg Order: {currencyCell(point.averageOrderAmount)}
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    Item Count: {numberCell(point.invoiceItemCount)}
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    Margin: {currencyCell(point.estimatedMargin)}
                  </Typography>
                </Box>
              )
            })}
          </CardContent>
        </Card>
      )
    },
    [trendModel.chartSeries],
  )

  return (
    <Page>
      <Box className={classes.boxDateRange}>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
        >
          {PRESETS.map((item) => (
            <Button
              key={item.key}
              variant={preset === item.key ? 'contained' : 'outlined'}
              onClick={() => applyPreset(item.key)}
            >
              {item.label}
            </Button>
          ))}
          <Chip
            label="Custom Range"
            color={preset === 'custom' ? 'primary' : 'default'}
            variant={preset === 'custom' ? 'filled' : 'outlined'}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
        >
          <ButtonGroup size="small">
            <Button
              variant={viewMode === 'summary' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('summary')}
            >
              Summary
            </Button>
            <Button
              variant={viewMode === 'trend' ? 'contained' : 'outlined'}
              onClick={() => setViewMode('trend')}
            >
              Trend
            </Button>
          </ButtonGroup>

          <ButtonGroup size="small">
            <Button
              variant={groupBy === 'outlet' ? 'contained' : 'outlined'}
              onClick={() => setGroupBy('outlet')}
            >
              Outlet
            </Button>
            <Button
              variant={groupBy === 'driver' ? 'contained' : 'outlined'}
              onClick={() => setGroupBy('driver')}
            >
              Driver
            </Button>
          </ButtonGroup>

          <FormControl
            size="small"
            sx={{ minWidth: 170 }}
          >
            <InputLabel id="metric-select">Metric</InputLabel>
            <Select
              labelId="metric-select"
              value={metric}
              label="Metric"
              onChange={(event) => setMetric(event.target.value)}
            >
              {METRIC_OPTIONS.map((item) => (
                <MenuItem
                  key={item.key}
                  value={item.key}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box className={classes.dateRangePicker}>
            <span className={classes.spanDate}>{date}</span>
            <IconButton
              onClick={handleClick}
              size="small"
            >
              <DateRangeIcon />
            </IconButton>
          </Box>
          <DateRangePicker
            anchorEl={anchorEl}
            handleClose={handleClose}
            handleSelect={handleSelect}
            selectionRange={rangeDate}
          />
        </Stack>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        sx={{ mb: 2 }}
      >
        <Tab
          label="Overview"
          value="overview"
        />
        <Tab
          label="Detail"
          value="detail"
        />
      </Tabs>

      {activeTab === 'overview' && (
        <Grid
          container
          spacing={2}
        >
          {viewMode === 'trend' && (
            <>
              <Grid
                item
                xs={12}
                md={8}
              >
                <Card className={classes.chartCard}>
                  {reportStore.loadingTrend && <LinearProgress />}
                  <CardContent>
                    <Typography
                      variant="h3"
                      className={classes.cardTitle}
                    >
                      Monthly Trend ({selectedMetric.label})
                    </Typography>
                    {trendModel.hiddenSeriesCount > 0 && (
                      <Alert
                        severity="info"
                        sx={{ mb: 2 }}
                      >
                        Too many series detected. Showing top 8 and grouping{' '}
                        {trendModel.hiddenSeriesCount} into Others.
                      </Alert>
                    )}
                    {!chartRows.length && !reportStore.loadingTrend && (
                      <Alert severity="info">No trend data for selected date range.</Alert>
                    )}
                    {!!chartRows.length && (
                      <ResponsiveContainer
                        width="100%"
                        height={300}
                      >
                        <LineChart data={chartRows}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="period" />
                          <YAxis />
                          <Tooltip content={renderTooltip} />
                          {visibleSeries.map((series, index) => (
                            <Line
                              key={series.id}
                              dataKey={series.dataKey}
                              name={series.name}
                              stroke={
                                pinnedSeriesId === series.id
                                  ? '#d32f2f'
                                  : `hsl(${(index * 55) % 360}, 70%, 45%)`
                              }
                              strokeWidth={pinnedSeriesId === series.id ? 3 : 2}
                              dot={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
              >
                <Card>
                  <CardContent>
                    <Typography
                      variant="h3"
                      className={classes.cardTitle}
                    >
                      Legend
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search series"
                      value={legendSearch}
                      onChange={(event) => setLegendSearch(event.target.value)}
                      sx={{ mb: 2 }}
                    />
                    <Stack spacing={1}>
                      {trendModel.chartSeries.map((series) => (
                        <Chip
                          key={series.id}
                          label={series.name}
                          className={`${classes.legendItem} ${isolatedSeriesId === series.id ? classes.selectedLegend : ''}`}
                          color={pinnedSeriesId === series.id ? 'primary' : 'default'}
                          onClick={() =>
                            setIsolatedSeriesId((current) =>
                              current === series.id ? null : series.id,
                            )
                          }
                          onDelete={() =>
                            setPinnedSeriesId((current) =>
                              current === series.id ? null : series.id,
                            )
                          }
                          deleteIcon={<span>Pin</span>}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <Card>
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="h3">Ranked ({selectedPeriod || 'N/A'})</Typography>
                      <FormControl
                        size="small"
                        sx={{ minWidth: 140 }}
                      >
                        <InputLabel id="period-select">Month</InputLabel>
                        <Select
                          labelId="period-select"
                          label="Month"
                          value={selectedPeriod}
                          onChange={(event) => setSelectedPeriod(event.target.value)}
                        >
                          {(trendData?.periods || []).map((period) => (
                            <MenuItem
                              key={period}
                              value={period}
                            >
                              {period}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    <CustomDataGrid
                      rows={rankedRows}
                      columns={rankingColumns}
                      rowCount={rankedRows.length}
                      pageSize={Math.min(rankedRows.length || 10, 10)}
                      rowsPerPageOptions={[10, 20, 50]}
                      page={0}
                      hideFooterPagination
                      disableSelectionOnClick
                      onRowClick={(params) => setPinnedSeriesId(params.row.id)}
                      loading={reportStore.loadingTrend}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {viewMode === 'summary' && (
            <Grid
              item
              xs={12}
            >
              <Card>
                <CardContent>
                  <Typography
                    variant="h3"
                    className={classes.cardTitle}
                  >
                    Summary ({groupBy === 'outlet' ? 'Outlet' : 'Driver'})
                  </Typography>
                  <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                  >
                    Switch to Trend view for multi-series chart and MoM ranking.
                  </Alert>
                  <CustomDataGrid
                    rows={detailRows}
                    columns={columns}
                    rowCount={detailRows.length}
                    pageSize={detailRows.length || 10}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    page={0}
                    hideFooterPagination
                    disableSelectionOnClick
                    loading={reportStore.loading}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {activeTab === 'detail' && (
        <Grid
          container
          spacing={2}
        >
          <Grid
            item
            xs={12}
          >
            <Card>
              <CardContent>
                <Typography
                  variant="h3"
                  className={classes.cardTitle}
                >
                  Per Outlet
                </Typography>
                <CustomDataGrid
                  rows={summaryData.outlet}
                  columns={columns}
                  rowCount={summaryData.outlet.length}
                  pageSize={summaryData.outlet.length || 10}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  page={0}
                  hideFooterPagination
                  disableSelectionOnClick
                  loading={reportStore.loading}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <Card>
              <CardContent>
                <Typography
                  variant="h3"
                  className={classes.cardTitle}
                >
                  Per Driver
                </Typography>
                <CustomDataGrid
                  rows={summaryData.driver}
                  columns={columns}
                  rowCount={summaryData.driver.length}
                  pageSize={summaryData.driver.length || 10}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  page={0}
                  hideFooterPagination
                  disableSelectionOnClick
                  loading={reportStore.loading}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Page>
  )
}

export default observer(Analytics)
